/* The states a screen is in before it has anything of its own to show: reading the workspace, the
   read failing, and the account having no workspace. Screens lay out their own panels; this is
   only what stands in for them. */

/* A centred message that fills the body. `grid-row: 1 / -1` so it takes both of the shell's rows
   (the strip's `auto` and the panels' `1fr`) rather than sitting in the first. */
.state {
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: grid;
  place-items: center;
  align-content: center;
  min-height: 0;
  padding: 24px 0;
  color: var(--muted);
  font-size: var(--t-dense);
}

.failed {
  width: min(100%, 52ch);
}

/* No workspace is a statement of fact about the account, not a fault of the product, so it is
   a quiet bordered notice and not red text. */
.none {
  max-width: 52ch;
  margin: 0;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
  color: var(--ink-2);
  font-size: var(--t-body);
  line-height: 1.5;
  text-align: left;
}

/* Loading the workspace: the skeleton of a TablePage (ADR-0212) - the header card, a strip of tiles, and
   the table card with its filter line and rows - at the heights the loaded page has (the header `--page-head`, a
   tile `--tile-min`, a row 40px), so the real screen lands where the bones were and the page does not
   jump (design review, 2026-11-03: a 180px jump on Home). One pattern for every place: this is the
   state before the workspace is known, and a tile's own `loading` bar is the state after it. */
.loading {
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.head {
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  height: var(--page-head);
  padding: 0 16px;
}

.loading .badge {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-chip);
}

.loading .headTitle {
  width: 160px;
  max-width: 40%;
  height: 20px;
}

.strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--gap);
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: var(--tile-min);
  padding: 12px 14px;
}

/* The label row is as tall as the real one (the info control's 24px), the figure's line as tall as a
   figure's (32px). */
.tileLabel {
  display: flex;
  align-items: center;
  min-height: var(--target-min);
}

/* Prefixed `.loading`, so a bone's size outranks the global `.skeleton`'s 10px whichever stylesheet loads last. */
.loading .label {
  width: 45%;
}

.tileFigure {
  display: flex;
  align-items: center;
  height: var(--lh-24);
}

.loading .figure {
  width: 60%;
  height: 24px;
  border-radius: var(--radius-ctl);
}

.tableCard {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.filterLine {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
}

.loading .filter {
  width: 240px;
  max-width: 50%;
  height: 28px;
  border-radius: var(--radius-pill);
}

.row {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
}

.loading .line {
  width: 100%;
}

@media (max-width: 900px) {
  .strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
